import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { fetchFranchiseYearByYear, fetchHistoricalSeason } from '../../api/franchiseHistory';
import PlayerProfile from '../PlayerProfile';
import FranchiseMilestones from './FranchiseMilestones';

const { FiArrowLeft, FiTrendingUp, FiAward, FiCalendar, FiUsers, FiTarget } = FiIcons;

const CHAMPIONSHIPS = [2018, 2013, 2007, 2004, 1918, 1916, 1915, 1912, 1903];

const LeaderGroup = ({ title, leaders, onPlayerClick }) => {
  if (!leaders || leaders.length === 0) return null;
  return (
    <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)] flex flex-col">
      <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
        <span className="text-[10px] font-black uppercase tracking-widest text-[#d8ceb3]">{title}</span>
      </div>
      <div className="p-2">
        {leaders.map((l, i) => (
          <div 
            key={i} 
            onClick={() => l.person?.id && onPlayerClick(l.person.id, l.person.fullName)}
            className="flex items-center justify-between py-1.5 border-b border-[rgba(101,141,113,0.2)] last:border-0 cursor-pointer group hover:bg-[rgba(255,255,255,0.03)] px-1"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className={`text-[9px] font-black tabular-nums w-4 text-center shrink-0 ${i === 0 ? 'text-[#d8ceb3]' : 'text-[#658d71]'}`}>
                {i === 0 ? '★' : i + 1}
              </span>
              <span className="text-[10px] font-bold text-white uppercase tracking-wide truncate group-hover:text-[#d8ceb3] transition-colors">
                {l.person?.fullName?.split(' ').pop() || '--'}
              </span>
            </div>
            <span className="text-[11px] font-black tabular-nums text-[#a9c9b1] shrink-0 ml-2">
              {l.value ?? '--'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const FranchiseHistoryPage = () => {
  const navigate = useNavigate();
  const { yearParam } = useParams();
  
  const [trends, setTrends] = useState([]);
  const [loadingTrends, setLoadingTrends] = useState(true);
  
  const [selectedYear, setSelectedYear] = useState(yearParam || new Date().getFullYear().toString());
  const [seasonData, setSeasonData] = useState(null);
  const [loadingSeason, setLoadingSeason] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // 1. Fetch Year-by-Year Franchise Trends
  useEffect(() => {
    const loadTrends = async () => {
      setLoadingTrends(true);
      const { hitting, pitching } = await fetchFranchiseYearByYear(111);
      
      const seasonsMap = {};
      hitting.forEach(h => {
        const y = h.season;
        if (!seasonsMap[y]) seasonsMap[y] = { year: y };
        seasonsMap[y].runs = h.stat.runs;
        seasonsMap[y].hr = h.stat.homeRuns;
        seasonsMap[y].avg = parseFloat(h.stat.avg) || 0;
      });
      pitching.forEach(p => {
        const y = p.season;
        if (!seasonsMap[y]) seasonsMap[y] = { year: y };
        seasonsMap[y].era = parseFloat(p.stat.era) || 0;
        seasonsMap[y].so = p.stat.strikeOuts;
        seasonsMap[y].wins = p.stat.wins;
        seasonsMap[y].losses = p.stat.losses;
      });
      
      const combined = Object.values(seasonsMap).sort((a, b) => Number(a.year) - Number(b.year));
      setTrends(combined);
      setLoadingTrends(false);
    };
    loadTrends();
  }, []);

  // 2. Fetch Selected Season Data
  useEffect(() => {
    const loadSeason = async () => {
      if (!selectedYear) return;
      setLoadingSeason(true);
      const data = await fetchHistoricalSeason(selectedYear, 111);
      setSeasonData(data);
      setLoadingSeason(false);
      
      // Update URL silently for SEO / sharing
      window.history.replaceState(null, '', `#/franchise/${selectedYear}`);
    };
    loadSeason();
  }, [selectedYear]);

  // SEO & JSON-LD
  useEffect(() => {
    document.title = `Boston Red Sox Franchise History | ${selectedYear} Season | Green Monster`;
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = `Explore the Boston Red Sox franchise history, including all-time performance trends and detailed stats for the ${selectedYear} season.`;
  }, [selectedYear]);

  // Chart Option
  const chartOption = useMemo(() => {
    if (!trends.length) return null;
    return {
      backgroundColor: 'transparent',
      grid: { top: 40, right: 40, bottom: 40, left: 40 },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#1e3a29',
        borderColor: '#658d71',
        textStyle: { color: '#d8ceb3', fontSize: 11 }
      },
      legend: {
        data: ['Runs Scored', 'ERA'],
        textStyle: { color: '#a9c9b1', fontSize: 10 },
        bottom: 0
      },
      dataZoom: [
        { type: 'inside', start: 70, end: 100 },
        { type: 'slider', start: 70, end: 100, textStyle: { color: '#a9c9b1' }, borderColor: '#658d71', fillerColor: 'rgba(101,141,113,0.3)' }
      ],
      xAxis: {
        type: 'category',
        data: trends.map(t => t.year),
        axisLabel: { color: '#a9c9b1', fontSize: 10 },
        axisLine: { lineStyle: { color: '#658d71' } }
      },
      yAxis: [
        {
          type: 'value',
          name: 'Runs',
          nameTextStyle: { color: '#a9c9b1', fontSize: 10 },
          axisLabel: { color: '#a9c9b1', fontSize: 10 },
          splitLine: { lineStyle: { color: 'rgba(101,141,113,0.2)' } }
        },
        {
          type: 'value',
          name: 'ERA',
          nameTextStyle: { color: '#d8ceb3', fontSize: 10 },
          axisLabel: { color: '#d8ceb3', fontSize: 10 },
          splitLine: { show: false },
          inverse: true, // Lower ERA is better, so put it at the top
          min: 2.0,
          max: 6.0
        }
      ],
      series: [
        {
          name: 'Runs Scored',
          type: 'bar',
          data: trends.map(t => t.runs),
          itemStyle: { color: 'rgba(169,201,177,0.7)', borderColor: '#a9c9b1', borderWidth: 1 }
        },
        {
          name: 'ERA',
          type: 'line',
          yAxisIndex: 1,
          data: trends.map(t => t.era),
          smooth: true,
          symbol: 'none',
          lineStyle: { color: '#d8ceb3', width: 3 }
        }
      ]
    };
  }, [trends]);

  // Extract Season Record
  const seasonRecord = useMemo(() => {
    if (!seasonData?.standings?.records) return null;
    for (const record of seasonData.standings.records) {
      const teamRecord = record.teamRecords.find(t => t.team.id === 111);
      if (teamRecord) return teamRecord;
    }
    return null;
  }, [seasonData]);

  const handlePlayerClick = (id, name) => setSelectedPlayer({ id, name });

  return (
    <div className="min-h-screen bg-[#1e3a29] p-3 sm:p-6 md:p-10">
      <div className="max-w-6xl mx-auto flex flex-col gap-6">
        
        {/* Header */}
        <div className="flex items-center gap-4 border-b-2 border-[#658d71] pb-4">
          <button onClick={() => navigate('/')} className="p-2 sm:p-3 border border-[#658d71] text-[#d8ceb3] hover:bg-[#386646] transition-colors">
            <SafeIcon icon={FiArrowLeft} size={20} />
          </button>
          <div>
            <h1 className="text-2xl sm:text-4xl font-black uppercase tracking-widest text-[#d8ceb3] flex items-center gap-3">
              Franchise History
            </h1>
            <p className="text-[10px] sm:text-xs text-[#a9c9b1] font-bold uppercase tracking-widest mt-1">
              Boston Red Sox · Established 1901
            </p>
          </div>
        </div>

        {/* Milestones & Records */}
        {!loadingTrends && trends.length > 0 && (
          <FranchiseMilestones trends={trends} />
        )}

        {/* All-Time Trends Chart */}
        <div className="border-2 border-[#658d71] bg-[rgba(0,0,0,0.15)] flex flex-col mt-2">
          <div className="px-4 py-3 border-b border-[#658d71] bg-[#386646] flex items-center gap-2">
            <SafeIcon icon={FiTrendingUp} size={16} className="text-[#d8ceb3]" />
            <span className="text-sm font-black uppercase tracking-widest text-[#d8ceb3]">Historical Performance Trends</span>
          </div>
          <div className="p-4">
            {loadingTrends ? (
              <div className="h-[300px] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#658d71] border-t-[#d8ceb3] rounded-full animate-spin" />
              </div>
            ) : chartOption ? (
              <ReactECharts option={chartOption} style={{ height: '340px' }} />
            ) : (
              <div className="h-[300px] flex items-center justify-center text-[#658d71] text-xs uppercase tracking-widest">
                No trend data available
              </div>
            )}
          </div>
        </div>

        {/* Season Explorer */}
        <div className="border-2 border-[#658d71] bg-[rgba(0,0,0,0.15)] flex flex-col">
          <div className="px-4 py-3 border-b border-[#658d71] bg-[#386646] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiCalendar} size={16} className="text-[#d8ceb3]" />
              <span className="text-sm font-black uppercase tracking-widest text-[#d8ceb3]">Season Explorer</span>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-[10px] text-[#a9c9b1] font-bold uppercase tracking-widest">Select Year:</span>
              <select 
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="bg-[rgba(0,0,0,0.3)] border border-[#a9c9b1] text-white text-sm font-black p-1.5 focus:outline-none focus:border-[#d8ceb3]"
              >
                {trends.slice().reverse().map(t => (
                  <option key={t.year} value={t.year}>{t.year}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="p-4 border-b border-[rgba(101,141,113,0.3)] bg-[rgba(0,0,0,0.2)]">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest mr-2">Championships:</span>
              {CHAMPIONSHIPS.map(year => (
                <button 
                  key={year}
                  onClick={() => setSelectedYear(year.toString())}
                  className={`flex items-center gap-1 text-[9px] font-black px-2 py-1 border transition-colors ${selectedYear === year.toString() ? 'bg-[#d8ceb3] text-[#1e3a29] border-[#d8ceb3]' : 'bg-[rgba(216,206,179,0.1)] text-[#d8ceb3] border-[#658d71] hover:bg-[rgba(216,206,179,0.2)]'}`}
                >
                  <SafeIcon icon={FiAward} size={10} /> {year}
                </button>
              ))}
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {loadingSeason ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-8 h-8 border-2 border-[#658d71] border-t-[#d8ceb3] rounded-full animate-spin" />
                <span className="text-[#a9c9b1] text-xs tracking-widest uppercase">Loading {selectedYear} Season...</span>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div 
                  key={selectedYear}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-col gap-6"
                >
                  {/* Season Record Overview */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <div className="col-span-2 sm:col-span-1 border border-[#658d71] bg-[#386646] p-4 flex flex-col items-center justify-center">
                      <span className="text-3xl font-black text-[#d8ceb3]">{selectedYear}</span>
                      <span className="text-[10px] text-[#a9c9b1] font-bold uppercase tracking-widest mt-1">Season</span>
                    </div>
                    {[
                      { label: 'Record', val: seasonRecord ? `${seasonRecord.wins}-${seasonRecord.losses}` : '--' },
                      { label: 'Win %', val: seasonRecord?.winningPercentage ?? '--' },
                      { label: 'Rank', val: seasonRecord ? `#${seasonRecord.divisionRank || seasonRecord.leagueRank}` : '--' },
                      { label: 'Games Back', val: seasonRecord?.gamesBack === '-' ? '0.0' : (seasonRecord?.gamesBack ?? '--') }
                    ].map(s => (
                      <div key={s.label} className="border border-[#658d71] bg-[rgba(0,0,0,0.1)] p-3 flex flex-col items-center justify-center gap-1">
                        <span className="text-xl sm:text-2xl font-black tabular-nums text-white">{s.val}</span>
                        <span className="text-[9px] uppercase tracking-widest text-[#658d71] font-bold">{s.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Leaders Grids */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Batting Leaders */}
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2 mb-1">
                        <SafeIcon icon={FiTarget} size={14} className="text-[#a9c9b1]" />
                        <h3 className="text-sm font-black uppercase tracking-widest text-[#a9c9b1]">Batting Leaders</h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <LeaderGroup 
                          title="Home Runs" 
                          leaders={seasonData?.leadersBatting?.leagueLeaders?.find(l => l.leaderCategory === 'homeRuns')?.leaders}
                          onPlayerClick={handlePlayerClick}
                        />
                        <LeaderGroup 
                          title="Batting Avg" 
                          leaders={seasonData?.leadersBatting?.leagueLeaders?.find(l => l.leaderCategory === 'battingAverage')?.leaders}
                          onPlayerClick={handlePlayerClick}
                        />
                        <LeaderGroup 
                          title="RBI" 
                          leaders={seasonData?.leadersBatting?.leagueLeaders?.find(l => l.leaderCategory === 'runsBattedIn')?.leaders}
                          onPlayerClick={handlePlayerClick}
                        />
                        <LeaderGroup 
                          title="OPS" 
                          leaders={seasonData?.leadersBatting?.leagueLeaders?.find(l => l.leaderCategory === 'onBasePlusSlugging')?.leaders}
                          onPlayerClick={handlePlayerClick}
                        />
                      </div>
                    </div>

                    {/* Pitching Leaders */}
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2 mb-1">
                        <SafeIcon icon={FiUsers} size={14} className="text-[#c4333e]" />
                        <h3 className="text-sm font-black uppercase tracking-widest text-[#c4333e]">Pitching Leaders</h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <LeaderGroup 
                          title="ERA" 
                          leaders={seasonData?.leadersPitching?.leagueLeaders?.find(l => l.leaderCategory === 'era')?.leaders}
                          onPlayerClick={handlePlayerClick}
                        />
                        <LeaderGroup 
                          title="Strikeouts" 
                          leaders={seasonData?.leadersPitching?.leagueLeaders?.find(l => l.leaderCategory === 'strikeouts')?.leaders}
                          onPlayerClick={handlePlayerClick}
                        />
                        <LeaderGroup 
                          title="Wins" 
                          leaders={seasonData?.leadersPitching?.leagueLeaders?.find(l => l.leaderCategory === 'wins')?.leaders}
                          onPlayerClick={handlePlayerClick}
                        />
                        <LeaderGroup 
                          title="Saves" 
                          leaders={seasonData?.leadersPitching?.leagueLeaders?.find(l => l.leaderCategory === 'saves')?.leaders}
                          onPlayerClick={handlePlayerClick}
                        />
                      </div>
                    </div>
                  </div>

                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>

      {selectedPlayer && (
        <PlayerProfile 
          playerId={selectedPlayer.id} 
          playerName={selectedPlayer.name} 
          onClose={() => setSelectedPlayer(null)} 
        />
      )}
    </div>
  );
};

export default FranchiseHistoryPage;